/**
 * TrainingMonitoringConsentModal (TCX-8, K78-C)
 *
 * Shown BEFORE OptiBay launches a monitored Training Companion session when no
 * active durable consent is confirmed. Explicit opt-in is required; declining
 * does NOT open the training link or mint a monitoring token.
 *
 * Consent is recorded server-side (`log-training-monitoring-consent`) — this
 * modal is NOT the authority and never persists consent in localStorage. The
 * server mint/ingest gates remain the final authority.
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldCheck, AlertCircle } from 'lucide-react';

export interface TrainingMonitoringConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opt-in: write durable consent, then continue the monitored launch. */
  onOptIn: () => void;
  /** Decline/cancel: do NOT open training, do NOT mint a token. */
  onDecline: () => void;
  submitting?: boolean;
  error?: string | null;
}

// The exact monitoring boundaries the technician is consenting to. Kept as data
// so tests can assert each boundary is shown. Wording matches the TCX-7 domain
// policy: we record whether the opened page matches the approved URL/domain, but
// mismatched observations cannot independently verify training (the effective
// policy may be permissive, strict, or allowlist).
export const TRAINING_MONITORING_CONSENT_BOUNDARIES = [
  'Only training sessions you launch from OptiBay are monitored — never your general browsing or unrelated tabs.',
  'Whether the opened page matches the approved training URL / domain (policy may be permissive, strict, or allowlist; a mismatch can never verify training on its own).',
  'The page title of the approved training, where available.',
  'Active-tab state, visible time, and scroll depth on the approved training tab.',
  'Video readiness / progress, when the training provides it.',
  'Training-session state and completion evidence.',
] as const;

// What is explicitly NOT collected. Shown so consent is informed. Kept as data
// so tests can assert each exclusion is displayed.
export const TRAINING_MONITORING_NOT_COLLECTED = [
  'Passwords or form-field contents.',
  'Clipboard contents.',
  'Your browsing history or unrelated tabs.',
  'Audio or general screen recording.',
] as const;

export function TrainingMonitoringConsentModal({
  open,
  onOpenChange,
  onOptIn,
  onDecline,
  submitting = false,
  error = null,
}: TrainingMonitoringConsentModalProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing the dialog by any means (X, overlay, Esc) is a decline.
        if (!next) onDecline();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <DialogTitle>Allow training monitoring?</DialogTitle>
          </div>
          <DialogDescription>
            To support your training recommendations and record completion evidence, OptiBay
            can monitor the training sessions you launch from here. Here is exactly what that
            means:
          </DialogDescription>
        </DialogHeader>

        <p className="text-xs font-medium text-foreground">What may be collected from the launched tab</p>
        <ul className="space-y-2 text-sm text-muted-foreground">
          {TRAINING_MONITORING_CONSENT_BOUNDARIES.map((line) => (
            <li key={line} className="flex gap-2">
              <span aria-hidden className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <p className="text-xs font-medium text-foreground">What is never collected</p>
        <ul className="space-y-2 text-sm text-muted-foreground">
          {TRAINING_MONITORING_NOT_COLLECTED.map((line) => (
            <li key={line} className="flex gap-2">
              <span aria-hidden className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-500/60" />
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <p className="text-xs text-muted-foreground">
          You can change this later in Settings. Declining will not open this training session and
          does not mark any training complete.
        </p>

        {error ? (
          <div role="alert" className="flex items-center gap-2 rounded-md bg-destructive/10 p-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        ) : null}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onDecline} disabled={submitting}>
            Not now
          </Button>
          <Button onClick={onOptIn} disabled={submitting}>
            {submitting ? 'Saving…' : 'Agree & continue'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
